/* eslint-disable */
import log from '@/logger';

let currentAnimationFrame = null;
let currentInterruptionListeners = null;

const cancelCurrentScroll = () => {
  if (currentAnimationFrame) {
    cancelAnimationFrame(currentAnimationFrame);
    currentAnimationFrame = null;
  }
  if (currentInterruptionListeners) {
    const { container, listeners, windowListeners } = currentInterruptionListeners;
    Object.keys(listeners).forEach((event) => {
      container.removeEventListener(event, listeners[event], { passive: true });
    });
    if (windowListeners) {
      Object.keys(windowListeners).forEach((event) => {
        window.removeEventListener(event, windowListeners[event], { passive: true });
      });
    }
    currentInterruptionListeners = null;
  }
};

export default function scrollTo(destination, duration = 200, easing = 'linear', container = null, topOffset = 0, callback) {
  if (typeof container === 'function') {
    callback = container;
    container = null;
    topOffset = 0;
  }

  if (typeof topOffset === 'function') {
    callback = topOffset;
    topOffset = 0;
  }

  cancelCurrentScroll();

  const getScrollParent = (node) => {
    if (node === null || node === undefined || node === document.body || node === document.documentElement) {
      return window;
    }

    if (node.scrollHeight > node.clientHeight) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if (overflowY !== 'visible' && overflowY !== 'hidden') {
        return node;
      }
    }

    return getScrollParent(node.parentNode);
  };

  const easings = {
    linear(t) {
      return t;
    },
    easeInQuad(t) {
      return t * t;
    },
    easeOutQuad(t) {
      return t * (2 - t);
    },
    easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
    easeInCubic(t) {
      return t * t * t;
    },
    easeOutCubic(t) {
      return (--t) * t * t + 1;
    },
    easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    },
    easeInQuart(t) {
      return t * t * t * t;
    },
    easeOutQuart(t) {
      return 1 - (--t) * t * t * t;
    },
    easeInOutQuart(t) {
      return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
    },
    easeInQuint(t) {
      return t * t * t * t * t;
    },
    easeOutQuint(t) {
      return 1 + (--t) * t * t * t * t;
    },
    easeInOutQuint(t) {
      return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;
    }
  };

  let actualContainer = container;
  if (!actualContainer && typeof destination !== 'number' && destination.nodeType) {
    actualContainer = getScrollParent(destination.parentNode);
  }
  if (!actualContainer) {
    actualContainer = window;
  }

  const isWindow = actualContainer === window;

  const getScrollPos = () => {
    if (isWindow) {
      return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
    }
    return actualContainer.scrollTop;
  };

  const start = getScrollPos();
  let prevScrollPos = start;
  let scrollCheckCount = 20;
  const startTime = 'now' in window.performance ? performance.now() : new Date().getTime();

  let destinationOffset;
  if (typeof destination === 'number') {
    destinationOffset = destination;
  } else if (isWindow) {
    destinationOffset = Math.round(destination.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) - topOffset);
  } else {
    destinationOffset = destination.offsetTop - topOffset;
  }

  let maxScroll;
  let viewportHeight;
  if (isWindow) {
    maxScroll = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    ) - window.innerHeight;
    viewportHeight = window.innerHeight;
  } else {
    maxScroll = actualContainer.scrollHeight - actualContainer.clientHeight;
    viewportHeight = actualContainer.clientHeight;
  }

  const destinationOffsetToScroll = Math.max(0, Math.min(destinationOffset, maxScroll));

  log.info('[scrollTo] Starting scroll to:', destination, {
    duration,
    easing,
    container: actualContainer,
    isWindow,
    topOffset,
    start,
    destinationOffset,
    maxScroll,
    destinationOffsetToScroll,
  });

  const performScroll = (pos) => {
    if (isWindow) {
      window.scroll(0, pos);
    } else {
      actualContainer.scrollTop = pos;
    }
  };

  if ('requestAnimationFrame' in window === false) {
    log.info('[scrollTo] requestAnimationFrame not supported, jumping to position');
    performScroll(destinationOffsetToScroll);
    if (callback) {
      callback();
    }
    return;
  }

  const interruptionEvents = ['wheel', 'touchstart', 'mousedown', 'keydown', 'MSPointerDown', 'pointerdown', 'touchmove'];
  const interruptionTarget = isWindow ? window : actualContainer;

  const onUserInterruption = () => {
    const now = 'now' in window.performance ? performance.now() : new Date().getTime();
    if (now - startTime < 150) {
      log.info('[scrollTo] Ignoring interruption during first 150ms');
      return;
    }

    log.info('[scrollTo] User interrupted scroll');
    cancelCurrentScroll();
  };

  const setupListeners = (target, listenersMap) => {
    interruptionEvents.forEach((event) => {
      listenersMap[event] = onUserInterruption;
      target.addEventListener(event, onUserInterruption, { passive: true });
    });
  };

  const containerListeners = {};
  setupListeners(interruptionTarget, containerListeners);
  let windowListeners = null;
  if (!isWindow) {
    windowListeners = {};
    setupListeners(window, windowListeners);
  }

  currentInterruptionListeners = {
    container: interruptionTarget,
    listeners: containerListeners,
    windowListeners,
  };

  log.info('[scrollTo] Starting animation');

  function scroll() {
    const now = 'now' in window.performance ? performance.now() : new Date().getTime();
    const time = Math.min(1, ((now - startTime) / duration));
    const timeFunction = easings[easing](time);
    const nextPos = Math.ceil((timeFunction * (destinationOffsetToScroll - start)) + start);

    performScroll(nextPos);

    const currentScrollPos = getScrollPos();

    // Check if scroll is stuck (position not updating as expected)
    // Only if we're not already at the destination and expecting to move
    const hasPositionChanged = Math.abs(prevScrollPos - currentScrollPos) >= 1;
    const shouldHaveMoved = Math.abs(nextPos - prevScrollPos) > 1;

    if (!hasPositionChanged && shouldHaveMoved) {
      scrollCheckCount--;
      log.info('[scrollTo] Scroll might be stuck:', {
        prevScrollPos,
        currentScrollPos,
        nextPos,
        scrollCheckCount,
      });
    }

    if (Math.abs(currentScrollPos - destinationOffsetToScroll) <= 1 || scrollCheckCount < 0) {
      log.info('[scrollTo] Scroll finished at:', currentScrollPos, {
        reason: Math.abs(currentScrollPos - destinationOffsetToScroll) <= 1 ? 'reached destination' : 'scroll stuck (scrollCheckCount < 0)',
      });
      cancelCurrentScroll();
      if (callback) {
        callback();
      }
      return;
    }

    prevScrollPos = currentScrollPos;
    currentAnimationFrame = requestAnimationFrame(scroll);
  }

  // wait a bit for initialisation to avoid premature interruption from touch events
  setTimeout(() => {
    currentAnimationFrame = requestAnimationFrame(scroll);
  }, 50);
}



// WEBPACK FOOTER //
// ./src/utils/scrollTo.js